import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from '@/components/ui/sonner'
import { ThemeProvider } from '@/components/providers/ThemeProvider'
import App from './App'
import './styles/globals.css'
import { apiClient } from '@/services/api/client'
import { useAuthStore } from '@/stores/authStore'
import { usePlatformContextStore } from '@/stores/platformContextStore'
import { isPlatformRole } from '@/types/auth'
import { queryClient } from '@/lib/queryClient'
import '@/stores/timezoneSync'
import { installDateTimezoneDefaults } from '@/utils/timezone'

// Render every Date#toLocale*String in the user's chosen timezone (see utils/timezone.ts).
installDateTimezoneDefaults()

// Wire the platform impersonation context getter into the API client.
//
// The getter returns the tenant ID that a platform admin has chosen to
// impersonate, which is sent as X-Target-Tenant-ID on every request.
// It returns null for all non-platform users so they never send this header
// — their tenant context is embedded in the JWT tid claim.
//
// This is registered at module scope (before React renders) so that
// page-refresh requests that fire during app initialisation already carry
// the correct header. Zustand's getState() is safe to call here because
// it does not require a React context.
apiClient.registerPlatformContextGetter(() => {
  const user = useAuthStore.getState().user
  // Only platform-role users use the header override mechanism.
  // Regular tenant users must not send X-Target-Tenant-ID.
  if (!user || !isPlatformRole(user.role)) {
    return null
  }
  return usePlatformContextStore.getState().selectedTenantId
})

const isDev = import.meta.env.DEV

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <App />
        <Toaster richColors position="bottom-right" />
      </ThemeProvider>
      {isDev && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>
)
